
import { useState } from "react";
import { FilmInventory, FilmFormValues } from "@/types/inventory";
import { useIsMobile } from "@/hooks/use-mobile";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DesktopInventoryContent } from "./DesktopInventoryContent";
import { MobileInventoryContent } from "./MobileInventoryContent";
import { FilmCard } from "./FilmCard";
import { FilmSearchAndAdd } from "./FilmSearchAndAdd";
import { FilmTabButtons } from "./FilmTabButtons";
import { FilmEmptyState } from "./FilmEmptyState";
import { useFilmInventory } from "@/hooks/useFilmInventory";
import { useTranslation } from "@/hooks/useTranslation";

export function InventoryTable() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<'unexposed' | 'exposed'>('unexposed');
  const isMobile = useIsMobile();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { t } = useTranslation();

  const { 
    films, 
    loading, 
    error, 
    activeSort,
    sortDirection,
    fetchFilms,
    handleAddFilm,
    handleSortChange,
    handleDirectionChange
  } = useFilmInventory();

  const sortOptions = [
    { label: t('brand'), value: "brand" },
    { label: t('name'), value: "film_name" },
    { label: t('type'), value: "film_type" },
    { label: "ISO", value: "iso" },
    { label: t('quantity'), value: "quantity" },
    { label: t('expiration_date'), value: "expiration_date" },
  ];

  const renderFilmCard = (film: FilmInventory, index: number) => (
    <FilmCard 
      key={film.id || index} 
      film={film} 
      showTransfer={activeTab !== 'exposed'} 
      onActionComplete={fetchFilms}
    />
  );

  const filteredFilms = searchQuery 
    ? films.filter(film => 
        film.film_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        film.brand.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : films;

  const tabFilteredFilms = filteredFilms.filter(film => 
    activeTab === 'unexposed' 
      ? film.status !== 'exposed' 
      : film.status === 'exposed'
  );

  const sortedFilms = [...tabFilteredFilms].sort((a, b) => {
    const fieldA = a[activeSort as keyof FilmInventory];
    const fieldB = b[activeSort as keyof FilmInventory];
    
    if (fieldA === null) return sortDirection === 'asc' ? -1 : 1;
    if (fieldB === null) return sortDirection === 'asc' ? 1 : -1;
    
    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      return sortDirection === 'asc' 
        ? fieldA.localeCompare(fieldB) 
        : fieldB.localeCompare(fieldA);
    }
    
    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-4">
      <FilmSearchAndAdd 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAddFilm={handleAddFilm}
        sortOptions={sortOptions}
        activeSort={activeSort}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        onDirectionChange={handleDirectionChange}
        isAddDialogOpen={isAddDialogOpen}
        setIsAddDialogOpen={setIsAddDialogOpen}
      />
      
      <FilmTabButtons 
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      
      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('error')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : tabFilteredFilms.length === 0 ? (
        <FilmEmptyState 
          searchQuery={searchQuery}
          onAddClick={() => setIsAddDialogOpen(true)}
        />
      ) : (
        <>
          {isMobile ? (
            <MobileInventoryContent 
              data={sortedFilms} 
              sortField={activeSort || ""}
              sortOrder={sortDirection}
              onSort={handleSortChange}
              onSortOrderChange={handleDirectionChange}
              onActionComplete={fetchFilms}
              renderItem={renderFilmCard}
              sortOptions={sortOptions}
              showTransfer={activeTab !== 'exposed'}
            />
          ) : (
            <DesktopInventoryContent 
              data={sortedFilms} 
              onSort={handleSortChange}
              onActionComplete={fetchFilms}
              showTransfer={activeTab !== 'exposed'}
            />
          )}
        </>
      )}
    </div>
  );
}
